import { zodResolver } from '@hookform/resolvers/zod';
import { showroomSchema, SHOWROOM_PADRAO, type Showroom } from '@plane/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Field } from '@/components/field';
import { CampoDeImagem } from '@/modules/site-content/components/campo-de-imagem';
import { useShowroom, useSaveShowroom } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function ShowroomPage() {
  const consulta = useShowroom();
  const salvar = useSaveShowroom();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? SHOWROOM_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(showroom) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(showroom)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Showroom;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (showroom: Showroom) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Showroom>({
    resolver: zodResolver(showroomSchema),
    defaultValues: inicial,
  });

  const foto = watch('foto');

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit(aoEnviar)(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Showroom</h1>
        <p className="mt-3 text-sm text-ink-soft">A quinta seção, onde fica o convite à visita.</p>
      </div>

      {/*
        A ausência é tão importante quanto os campos: quem procurar "endereço"
        aqui e não achar precisa saber para onde ir, senão preenche de novo em
        outro lugar e as duas cópias divergem.
      */}
      <p className="border-l-2 border-brand-600 pl-3 text-sm text-ink-soft">
        Endereço, horário, telefone e as coordenadas do mapa <strong>não ficam aqui</strong> — são
        os mesmos do rodapé, e você edita em{' '}
        <Link to="/empresa" className="text-brand-700 underline">
          Empresa e contato
        </Link>
        .
      </p>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os textos abaixo vêm do protótipo e a foto está vazia.
        </p>
      )}

      <Field
        id="kicker"
        label="Etiqueta acima do título"
        placeholder="05 — Onde a gente fica"
        error={errors.kicker?.message}
        {...register('kicker')}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="titulo" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Título
        </label>
        <textarea
          id="titulo"
          rows={2}
          aria-invalid={errors.titulo ? true : undefined}
          aria-describedby="titulo-ajuda"
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
          {...register('titulo')}
        />
        <p id="titulo-ajuda" className="text-xs text-ink-soft">
          Quebre a linha onde o título deve quebrar. <code>*assim*</code> destaca a palavra em verde
          itálico.
        </p>
        {errors.titulo && (
          <p role="alert" className="text-xs text-brass">
            {errors.titulo.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="lead" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Parágrafo
        </label>
        <textarea
          id="lead"
          rows={4}
          aria-invalid={errors.lead ? true : undefined}
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
          {...register('lead')}
        />
        {errors.lead && (
          <p role="alert" className="text-xs text-brass">
            {errors.lead.message}
          </p>
        )}
      </div>

      <div>
        <Field
          id="etiquetaDoMapa"
          label="Etiqueta sobre o mapa"
          placeholder="Plane Móveis · showroom + fábrica"
          error={errors.etiquetaDoMapa?.message}
          {...register('etiquetaDoMapa')}
        />
        <p className="mt-1.5 text-xs text-ink-soft">
          Aparece no canto do mapa. Deixe vazio para não escrever nada.
        </p>
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Foto do showroom
        </legend>
        <p className="mt-1.5 text-xs text-ink-soft">Fica ao lado do mapa, deitada.</p>

        <div className="mt-3">
          <CampoDeImagem
            mediaId={foto}
            aoTrocar={(id) => setValue('foto', id, { shouldValidate: true })}
          />
        </div>
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}
